/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// @mui material components
import { Grid, Tabs, Tab, Box, Card } from "@mui/material";
import { tabsClasses } from '@mui/material/Tabs';

// Soft UI Dashboard PRO React example components
import DashboardLayout from "../../../nextus/Dashboards/DashboardLayout";

import DashboardCardLayout from "../../../nextus/Dashboards/DashboardCardLayout";

import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "nextus/Tables/DevicesTable";

import {
    QueryClient,
    QueryClientProvider,
    useQuery,
} from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import bksAPI from 'api/bks';
import userAPI from 'api/user';
import { calculateBattery } from "helper/device";

import { useNavigate } from 'react-router-dom';
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";

import PropTypes from 'prop-types';
import { useEffect, useState } from "react";
import moment from "moment";
import { processDevicesData } from "helper/device";
import { getUserAccessLevel } from "helper/user";

import navigationData from "./pages/navigation";
import deviceInfo from "./pages/device_info";
import cameraFeeds from "./pages/camera_feeds";
import calendarEvents from "./pages/calendar_events";
import reportingInfo from "./pages/reporting";
import accountInfo from "./pages/account";
import { useSoftUIController } from "context";

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
            style={{ height: 'calc(100% - 35px)' }}
        >
            {value === index && <Box sx={{ p: 3, pb: 0, height: 'calc(100% - 24px)' }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`
    };
}

function wrapper(content, deviceStyle) {
    return (
        <SoftBox p={{ xxs: 1, xs: 3 }} height={{ xxs: 'calc(100% - 16px)', xs: 'calc(100% - 50px)' }}
            sx={({ palette: { white }, boxShadows: { navbarBoxShadow }, borders: { borderRadius }, functions: { rgba, pxToRem } }) => ({
                boxShadow: navbarBoxShadow,
                backdropFilter: `saturate(200%) blur(${pxToRem(4)})`,
                // backgroundColor: rgba(white.main, 0.8),
                borderRadius: borderRadius.xl,
                overflowY: 'auto',
                '::-webkit-scrollbar': {
                    backgroundColor: 'transparent',
                    width: '8px',
                },
                '::-webkit-scrollbar-track': { /* background of the scrollbar except button or resizer */
                    backgroundColor: 'transparent',
                },
                '::-webkit-scrollbar-thumb': { /* scrollbar itself */
                    backgroundColor: '#babac0',
                    borderRadius: '6px',
                    border: `1px solid ${rgba(white.main, 0.8)}`,
                },
                '::-webkit-scrollbar-button': { /* set button(top and bottom of the scrollbar) */
                    display: 'none',
                },
            })}
        >
            <SoftBox sx={{ height: '100%', overflow: "visible", backgroundColor: 'transparent' }}>
                <SoftBox pb={3} lineHeight={1}>
                    <SoftTypography pl={1} variant="h4">
                        Help on Nucleus
                    </SoftTypography>
                </SoftBox>
                {content}
            </SoftBox>
        </SoftBox>
    );
}

function NucleusHelp({ map, navTitle, setNavTitle, sidebarState, setSidebarState }) {

    const navigate = useNavigate();
    const [tabValue, setTabValue] = useState(0);
    const [controller, dispatch] = useSoftUIController();
    const { deviceStyle } = controller;

    const handleTabClick = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const { user } = useAuth0();
    bksAPI.loadToken(useAuth0());
    userAPI.loadToken(useAuth0());

    const selected_user = useQuery({
        queryKey: ['getUserByEmail', user.email.toLowerCase()],
        queryFn: async () => {
            return await userAPI.getUserByEmail(user.email.toLowerCase());
        }
    });

    useEffect(() => {
        setSidebarState('sidenav');
        setNavTitle(['Help']);
    }, [sidebarState, navTitle]);

    if (selected_user.isLoading) {
        return wrapper((
            <SoftTypography variant="h5" fontWeight="medium">
                Loading...
            </SoftTypography>
        ), deviceStyle);
    }

    if (selected_user.error) {
        return wrapper((
            <SoftTypography variant="h5" fontWeight="medium">
                {"An error has occurred: " + selected_user.error.message}
            </SoftTypography>
        ), deviceStyle);
    }

    return wrapper((
        <SoftBox pb={{ xxs: 1, xs: 3 }}>
            <SoftBox pb={deviceStyle == 'mobile' ? 2 : 0}>
                <Tabs variant="scrollable" value={tabValue}
                    onChange={handleTabClick} aria-label="Help Tabs"
                    allowScrollButtonsMobile
                    variant="scrollable"
                    scrollButtons
                    sx={{
                        [`& .${tabsClasses.scrollButtons}`]: {
                            '&.Mui-disabled': { opacity: 0.3 },
                        },
                    }}
                >
                    <Tab sx={{ minWidth: "fit-content !important", padding: '16px !important' }} label="Navigation" {...a11yProps(0)} />
                    <Tab sx={{ minWidth: "fit-content !important", padding: '16px !important' }} label="Devices and Groups" {...a11yProps(1)} />
                    <Tab sx={{ minWidth: "fit-content !important", padding: '16px !important' }} label="Camera Feeds" {...a11yProps(2)} />
                    <Tab sx={{ minWidth: "fit-content !important", padding: '16px !important' }} label="Calendar & Events" {...a11yProps(3)} />
                    <Tab sx={{ minWidth: "fit-content !important", padding: '16px !important' }} label="Reports" {...a11yProps(4)} />
                    <Tab sx={{ minWidth: "fit-content !important", padding: '16px !important' }} label="Account and Help" {...a11yProps(5)} />
                    {getUserAccessLevel(selected_user.data.user.user_type, 'technician') && (
                        <Tab sx={{ minWidth: "fit-content !important", padding: '16px !important' }} label="Technician/Admin Features" {...a11yProps(6)} />
                    )}
                </Tabs>
            </SoftBox>
            <SoftBox m={deviceStyle == 'mobile' ? 0 : 3} sx={{ height: '100%' }}>
                <Card sx={{ height: '100%' }}>
                    <CustomTabPanel value={tabValue} index={0}>
                        {navigationData(deviceStyle)}
                    </CustomTabPanel>
                    <CustomTabPanel value={tabValue} index={1}>
                        {deviceInfo(deviceStyle)}
                    </CustomTabPanel>
                    <CustomTabPanel value={tabValue} index={2}>
                        {cameraFeeds(deviceStyle)}
                    </CustomTabPanel>
                    {(getUserAccessLevel(selected_user.data.user.user_type, 'editor') == false) && (
                        <CustomTabPanel value={tabValue} index={3}>
                            {calendarEvents.viewer_info(deviceStyle)}
                        </CustomTabPanel>
                    )}
                    {(getUserAccessLevel(selected_user.data.user.user_type, 'editor') && getUserAccessLevel(selected_user.data.user.user_type, 'technician') == false) && (
                        <CustomTabPanel value={tabValue} index={3}>
                            {calendarEvents.editor_info(deviceStyle)}
                        </CustomTabPanel>
                    )}
                    {getUserAccessLevel(selected_user.data.user.user_type, 'technician') && (
                        <CustomTabPanel value={tabValue} index={3}>
                            {calendarEvents.technician_admin_info(deviceStyle)}
                        </CustomTabPanel>
                    )}
                    <CustomTabPanel value={tabValue} index={4}>
                        {reportingInfo(deviceStyle)}
                    </CustomTabPanel>
                    <CustomTabPanel value={tabValue} index={5}>
                        {accountInfo(deviceStyle)}
                    </CustomTabPanel>
                    {getUserAccessLevel(selected_user.data.user.user_type, 'technician') && (
                        <CustomTabPanel value={tabValue} index={6}>

                        </CustomTabPanel>
                    )}
                </Card>
            </SoftBox>
        </SoftBox>
    ), deviceStyle);
}

NucleusHelp.propTypes = {
    map: PropTypes.object,
    navTitle: PropTypes.array,
    setNavTitle: PropTypes.func,
    sidebarState: PropTypes.string,
    setSidebarState: PropTypes.func,
}

export default NucleusHelp;
